import React from 'react';
import { Button } from '../ui/button';
import WorkItemFileDrop from './WorkItemFileDrop';
import WorkItemAttachmentView from './WorkItemAttachmentView';
import { pendingBlob, pendingMeta } from './workItemFileItems';

/**
 * Work Item files rail (R-7714) — firm uploads are internal by default.
 */
const WorkItemFilesTab = ({
  detail,
  addWorkItemFiles,
  requestAttachmentUpload,
  downloadWorkItemAttachment,
  removeWorkItemAttachment,
  renameWorkItemAttachment,
  showAlert,
  onItemUpdated,
  pendingFiles,
  setPendingFiles,
  client = false,
}) => {
  const files = detail.files || [];
  const canAdd = !client || detail.clientFilesOpen;
  const opts = client ? { client: true } : {};

  const onAdd = async () => {
    if (!pendingFiles?.length) return;
    const prepared = await requestAttachmentUpload(
      detail.id,
      {
        kind: 'files',
        files: pendingFiles.map(pendingMeta),
      },
      opts,
    );
    if (!prepared?.ok) {
      showAlert(true, prepared?.message || 'Upload prepare failed.', 'error', 5000);
      return;
    }
    const rows = prepared.attachments || (prepared.attachment ? [prepared.attachment] : []);
    try {
      await Promise.all(
        rows.map((meta, i) =>
          fetch(meta.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': pendingMeta(pendingFiles[i]).contentType },
            body: pendingBlob(pendingFiles[i]),
          }),
        ),
      );
    } catch (err) {
      showAlert(true, 'File upload failed.', 'error', 5000);
      return;
    }
    const result = await addWorkItemFiles(
      detail.id,
      rows.map(({ uploadUrl, ...rest }) => rest),
      opts,
    );
    if (result?.ok) {
      setPendingFiles([]);
      onItemUpdated(result.item);
    } else {
      showAlert(true, result?.message || 'Could not attach files.', 'error', 5000);
    }
  };

  return (
    <div className='space-y-3'>
      <p className='text-xs text-muted-foreground'>
        {client
          ? 'Files on this ask. You can attach files when you first raise a query.'
          : 'Files on the ask itself (not a message). Firm files stay internal to the team. Duplicate names are kept as separate files.'}
      </p>
      {files.length === 0 ? (
        <p className='text-sm text-muted-foreground'>No files yet.</p>
      ) : (
        <ul className='space-y-2'>
          {files.map((file) => (
            <li key={file.id} className='rounded border border-border px-2 py-1'>
              <WorkItemAttachmentView
                workItemId={detail.id}
                attachment={file}
                downloadWorkItemAttachment={downloadWorkItemAttachment}
                removeWorkItemAttachment={removeWorkItemAttachment}
                renameWorkItemAttachment={renameWorkItemAttachment}
                showAlert={showAlert}
                onItemUpdated={onItemUpdated}
                client={client}
              />
            </li>
          ))}
        </ul>
      )}
      {canAdd ? (
        <>
          <WorkItemFileDrop files={pendingFiles} onChange={setPendingFiles} />
          <Button size='sm' disabled={!pendingFiles?.length} onClick={onAdd}>
            Add files
          </Button>
        </>
      ) : null}
    </div>
  );
};

export default WorkItemFilesTab;
